import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CustomerComplaintsReasonsTemplate from "../../../templates/customer-complaints-reasons";
import {
  addCustomerComplaintReasonRequest,
  deleteCustomerComplaintReasonRequest,
  getCustomerComplaintReasonDetailsRequest,
  getCustomerComplaintReasonsRequest,
  updateCustomerComplaintReasonRequest,
} from "../../../services/modules/lookups/complaints/customerComplaintReasons";

function CustomerComplaintsReasonsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.customerComplaintReasons.customerComplaintReasons);
  const count = useSelector((state) => state.customerComplaintReasons.count);
  const details = useSelector((state) => state.customerComplaintReasons.customerComplaintReason);
  const load = useSelector((state) => state.customerComplaintReasons.load);

  //permissions
  const currentUser = useSelector((state) => state.authenticate.currentUserProfile);
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.order_complaints
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getCustomerComplaintReasonDetailsRequest({ customer_complaint_reason: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getCustomerComplaintReasonsRequest(requestData));
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(details?.created_at).format("DD-MM-YYYY HH:mm a")}</>,
      updated_at: <>{dayjs(details?.updated_at).format("DD-MM-YYYY HH:mm a")}</>,
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/customer-complaint-reasons",
      title: t("customer_complaint_reasons.title"),
    },
    type && {
      active: false,
      title:
        type === "details" ? t("customer_complaint_reasons.details") : id ? t("customer_complaint_reasons.edit") : t("customer_complaint_reasons.add"),
    },
  ];
  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/customer-complaint-reasons");
      !id && formik?.resetForm();
      // USE TOSTIFY TO CONFIRM ACTION
      toast.success(id ? t("customer_complaint_reasons.updated") : t("customer_complaint_reasons.created"));
    };
    const requestData = {
      body: {
        order_complaint_reason: {
          ...formik?.values,
        },
      },
      action,
    };

    if (id) {
      //update call
      dispatch(updateCustomerComplaintReasonRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(addCustomerComplaintReasonRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    //action to be called after successfull deletion
    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 }); //temporary until the backend sends the data correctly so I can update the state instead of re-fetching
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/customer-complaint-reasons");
      toast.success(t("customer_complaint_reasons.deleted"));
    };
    const requestData = {
      id,
      action, //dispatched on success
    };
    dispatch(deleteCustomerComplaintReasonRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all complaints
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  //details of a single complaint
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  const token = localStorage.getItem("token");
  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <CustomerComplaintsReasonsTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default CustomerComplaintsReasonsPage;
